import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { jobsApi, ApiError } from "@/api";
import type { JobListRow } from "@/types";
import { Button, Spinner, Pill, Toast, useToast } from "@/components/ui";
import { formatMoney } from "@/lib/money";
import "./archive.css";

/* ─── Archive-readiness ──────────────────────────────────────────────────────
 * A completed job is "ready to archive" when its fees are fully invoiced AND no
 * invoice is left outstanding. Both figures come straight off the list_v2 row
 * (server-side fee/invoice sums) — see JobListRow. When a job isn't ready we
 * surface the blocking reason(s) so the studio knows what to chase. */

interface Readiness {
  ready: boolean;
  /** Empty when ready; one entry per blocking reason otherwise. */
  reasons: string[];
}

function archiveReadiness(row: JobListRow): Readiness {
  const reasons: string[] = [];

  // An outstanding balance (unpaid, possibly overdue) blocks archiving — the job
  // still has money owed against it.
  if (row.has_unpaid_invoice) {
    const amount = formatMoney(row.total_outstanding);
    reasons.push(row.has_overdue_invoice ? `${amount} outstanding · overdue` : `${amount} outstanding`);
  }

  // Not fully invoiced — there's still contracted value that hasn't been billed.
  // A completed job with no fees at all can never be "fully invoiced", so it's
  // flagged for review rather than silently archived.
  if (!row.is_fully_invoiced) {
    if (row.total_agreed <= 0) {
      reasons.push("No fees recorded");
    } else {
      reasons.push(`${formatMoney(row.remaining_to_invoice)} still to invoice`);
    }
  }

  return { ready: reasons.length === 0, reasons };
}

/** One-line billing summary shown on every row (invoiced of agreed). */
function billingLine(row: JobListRow): string {
  if (!row.has_billing) return "No fees or invoices";
  if (row.total_agreed <= 0) return `${formatMoney(row.total_invoiced)} invoiced · no fees`;
  return `${formatMoney(row.total_invoiced)} of ${formatMoney(row.total_agreed)} invoiced`;
}

export function ArchiveWorkbench() {
  const { toast, showToast } = useToast();

  const [rows, setRows] = useState<JobListRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  // Per-job in-flight archive (single "Archive now"); also blanks during bulk.
  const [archivingIds, setArchivingIds] = useState<Set<number>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirmingBulk, setConfirmingBulk] = useState(false);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    jobsApi
      .listCompletedJobs()
      .then((items) => {
        if (!cancelled) setRows(items);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load completed jobs.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => load(), [load]);

  // Split the completed jobs into ready / not-ready, preserving the fetched
  // job-number order within each group.
  const { ready, notReady } = useMemo(() => {
    const r: JobListRow[] = [];
    const n: JobListRow[] = [];
    for (const row of rows ?? []) {
      (archiveReadiness(row).ready ? r : n).push(row);
    }
    return { ready: r, notReady: n };
  }, [rows]);

  /** Drop the archived jobs from the list — once status flips to "archived" they
   *  no longer belong in this completed-jobs view, so removing them is the honest
   *  optimistic update (and keeps the ready count + bulk button in sync). */
  function removeRows(ids: Iterable<number>) {
    const drop = new Set(ids);
    setRows((prev) => (prev ? prev.filter((row) => !drop.has(row.id)) : prev));
  }

  async function archiveOne(row: JobListRow) {
    if (bulkBusy || archivingIds.has(row.id)) return;
    setArchivingIds((prev) => new Set(prev).add(row.id));
    setError(undefined);
    try {
      await jobsApi.updateJobStatus(row.id, "archived");
      removeRows([row.id]);
      showToast(`Archived #${row.job_number}${row.job_name ? ` · ${row.job_name}` : ""}`, "info");
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Could not archive that job.";
      setError(msg);
      showToast(msg, "warn");
    } finally {
      setArchivingIds((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    }
  }

  async function archiveAllReady() {
    setConfirmingBulk(false);
    if (bulkBusy || ready.length === 0) return;
    setBulkBusy(true);
    setError(undefined);

    // Fire them together but settle independently so one failure doesn't strand
    // the rest; remove the succeeded ids and report the tally.
    const results = await Promise.allSettled(
      ready.map((row) => jobsApi.updateJobStatus(row.id, "archived").then(() => row.id)),
    );
    const archived: number[] = [];
    let failed = 0;
    for (const res of results) {
      if (res.status === "fulfilled") archived.push(res.value);
      else failed += 1;
    }
    removeRows(archived);
    setBulkBusy(false);

    if (failed === 0) {
      showToast(`Archived ${archived.length} job${archived.length === 1 ? "" : "s"}`, "info");
    } else {
      const msg = `Archived ${archived.length}, ${failed} failed — try again`;
      setError(msg);
      showToast(msg, "warn");
    }
  }

  return (
    <section className="archive-wb">
      <Link to="/staff/jobs" className="archive-wb__back">← Jobs</Link>

      <header className="archive-wb__head">
        <div>
          <h1>Archive jobs</h1>
          <p className="archive-wb__sub">
            {rows ? (
              <>
                {rows.length} completed job{rows.length === 1 ? "" : "s"} · {ready.length} ready to archive
              </>
            ) : (
              "Completed jobs that are fully invoiced and paid up are ready to archive."
            )}
          </p>
        </div>
        <Button
          variant="primary"
          loading={bulkBusy}
          disabled={bulkBusy || ready.length === 0}
          onClick={() => setConfirmingBulk(true)}
        >
          Archive all ready ({ready.length})
        </Button>
      </header>

      {error ? (
        <p className="leads__error" role="alert" onClick={() => setError(undefined)}>
          {error}
        </p>
      ) : null}

      {loading ? (
        <div className="archive-wb__loading">
          <Spinner label="Loading completed jobs…" />
        </div>
      ) : !rows || rows.length === 0 ? (
        <p className="archive-wb__empty">No completed jobs right now.</p>
      ) : (
        <>
          <section className="archive-wb__group">
            <h2 className="archive-wb__group-title">
              Ready to archive <span className="archive-wb__count">{ready.length}</span>
            </h2>
            {ready.length === 0 ? (
              <p className="archive-wb__muted">
                Nothing ready yet — clear the outstanding invoices and finish billing the jobs below.
              </p>
            ) : (
              <div className="archive-table" role="table" aria-label="Ready to archive">
                <div className="archive-table__head archive-table__row--ready" role="row">
                  <span role="columnheader">Job #</span>
                  <span role="columnheader">Job name</span>
                  <span role="columnheader">Client</span>
                  <span role="columnheader">Billing</span>
                  <span role="columnheader">Status</span>
                  <span role="columnheader" className="archive-table__action-col">Action</span>
                </div>
                {ready.map((row) => (
                  <div key={row.id} className="archive-table__row archive-table__row--ready" role="row">
                    <span role="cell" data-label="Job #" className="archive-table__ref numeric">
                      {row.job_number}
                    </span>
                    <span role="cell" data-label="Job name">
                      <Link to={`/staff/jobs/${row.id}`} className="archive-table__name-link">
                        {row.job_name || "—"}
                      </Link>
                    </span>
                    <span role="cell" data-label="Client">{row.client_display || "—"}</span>
                    <span role="cell" data-label="Billing" className="archive-table__billing">
                      {billingLine(row)}
                    </span>
                    <span role="cell" data-label="Status">
                      <Pill tone="success">Ready to archive</Pill>
                    </span>
                    <span role="cell" data-label="Action" className="archive-table__action-col">
                      <Button
                        variant="secondary"
                        size="sm"
                        loading={archivingIds.has(row.id)}
                        disabled={bulkBusy || archivingIds.has(row.id)}
                        onClick={() => archiveOne(row)}
                      >
                        Archive now
                      </Button>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="archive-wb__group">
            <h2 className="archive-wb__group-title">
              Not ready <span className="archive-wb__count">{notReady.length}</span>
            </h2>
            {notReady.length === 0 ? (
              <p className="archive-wb__muted">Every completed job is ready to archive.</p>
            ) : (
              <div className="archive-table" role="table" aria-label="Not ready to archive">
                <div className="archive-table__head archive-table__row--blocked" role="row">
                  <span role="columnheader">Job #</span>
                  <span role="columnheader">Job name</span>
                  <span role="columnheader">Client</span>
                  <span role="columnheader">Billing</span>
                  <span role="columnheader">Why not</span>
                </div>
                {notReady.map((row) => {
                  const { reasons } = archiveReadiness(row);
                  return (
                    <Link
                      key={row.id}
                      to={`/staff/jobs/${row.id}`}
                      state={{ tab: "invoices" }}
                      className="archive-table__row archive-table__row--blocked archive-table__row--link"
                      role="row"
                      title="Open the job's invoices"
                    >
                      <span role="cell" data-label="Job #" className="archive-table__ref numeric">
                        {row.job_number}
                      </span>
                      <span role="cell" data-label="Job name">{row.job_name || "—"}</span>
                      <span role="cell" data-label="Client">{row.client_display || "—"}</span>
                      <span role="cell" data-label="Billing" className="archive-table__billing">
                        {billingLine(row)}
                      </span>
                      <span role="cell" data-label="Why not" className="archive-table__reasons">
                        {reasons.map((reason) => (
                          <Pill
                            key={reason}
                            tone={reason.includes("overdue") ? "danger" : "neutral"}
                          >
                            {reason}
                          </Pill>
                        ))}
                      </span>
                    </Link>
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}

      {confirmingBulk ? (
        <div className="archive-wb__confirm-overlay" role="dialog" aria-modal="true">
          <div className="archive-wb__confirm">
            <h2>Archive {ready.length} job{ready.length === 1 ? "" : "s"}?</h2>
            <p>
              Every completed job that's fully invoiced with nothing outstanding will be moved to
              Archived. You can reopen any of them later from the job page.
            </p>
            <div className="archive-wb__confirm-actions">
              <Button variant="ghost" onClick={() => setConfirmingBulk(false)} disabled={bulkBusy}>
                Cancel
              </Button>
              <Button variant="primary" onClick={archiveAllReady} loading={bulkBusy}>
                Archive {ready.length}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <Toast toast={toast} />
    </section>
  );
}
